import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { 
  Receipt, Save, RefreshCw, FileText, Building2, Phone, MapPin, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TicketSettingsPanel = () => {
  const [settings, setSettings] = useState({
    business_name: 'PONT 10',
    business_subtitle: 'BISTRÓ · CAFÈ · BAR',
    address_line1: '',
    address_line2: '',
    postal_code: '',
    city: '',
    phone: '',
    email: '',
    nif_cif: '',
    footer_message1: '¡Gracias por su visita!',
    footer_message2: 'Esperamos verle pronto',
    show_date: true,
    show_table_number: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/ticket-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching ticket settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('admin_token');
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/ticket-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success('Configuración guardada');
      } else {
        toast.error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handlePreview = () => {
    // Open preview in new tab (uses mesa 1 as example)
    window.open(`${API_URL}/api/mesa/1/ticket`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Configurar Ticket</h2>
          <p className="text-sm text-gray-500">Personaliza el ticket que descarga el cliente</p>
        </div>
        <Button variant="outline" onClick={handlePreview} className="flex items-center gap-2">
          <FileText size={16} />
          <span className="hidden sm:inline">Vista previa</span>
        </Button>
      </div>

      {/* Business Info */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 size={20} />
            Datos del negocio
          </CardTitle>
          <CardDescription>Información que aparece en la cabecera del ticket</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="business_name">Nombre del negocio</Label>
              <Input
                id="business_name"
                value={settings.business_name}
                onChange={e => handleChange('business_name', e.target.value)}
                placeholder="PONT 10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="business_subtitle">Subtítulo</Label>
              <Input
                id="business_subtitle"
                value={settings.business_subtitle}
                onChange={e => handleChange('business_subtitle', e.target.value)}
                placeholder="BISTRÓ · CAFÈ · BAR"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="nif_cif">NIF/CIF</Label>
            <Input
              id="nif_cif"
              value={settings.nif_cif}
              onChange={e => handleChange('nif_cif', e.target.value)}
              placeholder="B12345678"
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Info */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin size={20} />
            Contacto y dirección
          </CardTitle>
          <CardDescription>Opcional - aparece debajo del nombre</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="address_line1">Dirección</Label>
            <Input
              id="address_line1"
              value={settings.address_line1}
              onChange={e => handleChange('address_line1', e.target.value)}
              placeholder="Carrer Example, 10"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="postal_code">Código postal</Label>
              <Input
                id="postal_code"
                value={settings.postal_code}
                onChange={e => handleChange('postal_code', e.target.value)}
                placeholder="17850"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">Población</Label>
              <Input
                id="city"
                value={settings.city}
                onChange={e => handleChange('city', e.target.value)}
                placeholder="Besalú"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Teléfono</Label>
            <Input
              id="phone"
              value={settings.phone}
              onChange={e => handleChange('phone', e.target.value)}
              placeholder="+34 972 000 000"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email visible al cliente</Label>
            <Input
              id="email"
              type="email"
              value={settings.email}
              onChange={e => handleChange('email', e.target.value)}
              placeholder="hola@pont10.cat"
            />
          </div>
        </CardContent>
      </Card>

      {/* Footer Messages */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageSquare size={20} />
            Mensajes de pie
          </CardTitle>
          <CardDescription>Texto que aparece al final del ticket</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="footer_message1">Mensaje 1</Label>
            <Input
              id="footer_message1"
              value={settings.footer_message1}
              onChange={e => handleChange('footer_message1', e.target.value)}
              placeholder="¡Gracias por su visita!"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="footer_message2">Mensaje 2</Label>
            <Input
              id="footer_message2"
              value={settings.footer_message2}
              onChange={e => handleChange('footer_message2', e.target.value)}
              placeholder="Esperamos verle pronto"
            />
          </div>
        </CardContent>
      </Card>

      {/* Display Options */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Receipt size={20} />
            Opciones de visualización
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Mostrar número de mesa</Label>
              <p className="text-sm text-gray-500">Incluir el número de mesa en el ticket</p>
            </div>
            <Switch
              checked={settings.show_table_number}
              onCheckedChange={value => handleChange('show_table_number', value)}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <Label>Mostrar fecha y hora</Label>
              <p className="text-sm text-gray-500">Incluir la fecha del pedido</p>
            </div>
            <Switch
              checked={settings.show_date}
              onCheckedChange={value => handleChange('show_date', value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
        >
          {saving ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
          Guardar cambios
        </Button>
      </div>
    </div>
  );
};

export default TicketSettingsPanel;
